// frontend/src/components/TripTrackingMap.js
// See backend/docs/2026-01-06-SEGMENT_VARIANT_MAP_DISPLAY.md for segment variant display logic

import React from 'react';
import { Polyline, Tooltip } from 'react-leaflet';
import MapWithTooltips from './MapWithTooltips';
import MapControls from './MapControls';
import TripRouteSegments from './TripRouteSegments';
import Modal from './Modal';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferenceContext';
import { GitBranch } from 'lucide-react';
import { isAdmin } from '../utils/roleUtils';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import { getTripDisplayStops } from '../utils/tripDisplayStops';
import { formatDistanceMeters, getZeroDistanceLabel } from '../utils/distanceUtils';
import { parseNullableNonNegativeMetric } from '../utils/nullableMetric';
import { isTripEntityMismatch } from '../utils/entityMatch';
import logger from '../utils/logger';

/**
 * Determine if user can select segment variants based on trip status and role.
 * See docs/2026-01-06-SEGMENT_VARIANT_MAP_DISPLAY.md for full logic.
 */
const canSelectVariants = (status, role) => {
  // Terminal states - no selection allowed
  if (['completed', 'canceled'].includes(status)) return false;

  // Admin-level roles can select in all active states
  if (isAdmin(role)) {
    return ['scheduled', 'dispatched', 'in_progress'].includes(status);
  }

  // Driver can select once dispatched
  if (role === 'driver') {
    return ['dispatched', 'in_progress'].includes(status);
  }

  // Users cannot select variants
  return false;
};

const getWaypointOrderValue = (waypoint = {}) => {
  const effectiveSequence = Number(waypoint.effective_sequence);
  if (Number.isFinite(effectiveSequence)) return effectiveSequence;

  const sequenceNumber = Number(waypoint.sequence_number);
  return Number.isFinite(sequenceNumber) ? sequenceNumber : Number.MAX_SAFE_INTEGER;
};

const hasStopId = (stopId) => stopId !== null && stopId !== undefined && stopId !== '';

const isWaypointForStop = (waypoint = {}, stopId = null) => {
  if (!hasStopId(stopId)) return false;

  const waypointId = Number(waypoint.waypoint_id ?? waypoint.id);
  const selectedStopId = Number(stopId);
  return Number.isFinite(waypointId)
    && Number.isFinite(selectedStopId)
    && waypointId === selectedStopId;
};

const isRouteDetailWaypoint = (waypoint = {}) => waypoint.point_type === 'waypoint';
const STALE_ROUTE_CHOICE_CODES = new Set([
  'TRIP_PLAN_SELECTION_ALREADY_SELECTED',
  'TRIP_PLAN_SELECTION_UNAVAILABLE',
  'WAYPOINT_REORDER_STATE_CHANGED',
  'WAYPOINT_REORDER_VERSION_CONFLICT'
]);
const CLOSE_ONLY_ROUTE_CHOICE_CODES = new Set([
  'WAYPOINT_REORDER_POSITION_FIXED',
  'WAYPOINT_REORDER_STATUS_INELIGIBLE'
]);
const ROUTE_CHOICE_ERROR_KEYS = {
  TRIP_PLAN_SELECTION_ALREADY_SELECTED: 'routeChoiceAlreadySelected',
  TRIP_PLAN_SELECTION_UNAVAILABLE: 'routeChoiceUnavailable',
  WAYPOINT_REORDER_STATE_CHANGED: 'routeChoiceStateChanged',
  WAYPOINT_REORDER_VERSION_CONFLICT: 'routeChoiceVersionConflict',
  WAYPOINT_REORDER_POSITION_FIXED: 'routeChoicePositionFixed',
  WAYPOINT_REORDER_STATUS_INELIGIBLE: 'routeChoiceStatusIneligible'
};

const getRouteChoiceKey = (fromWaypointId, alternative = {}) => [
  fromWaypointId,
  alternative.to_waypoint_id,
  alternative.archived_type,
  alternative.variant ?? 'variant_null',
  alternative.isSequential ? 'is_sequential_1' : 'is_sequential_0'
].join('-');

const TripTrackingMap = ({
  selectedRoute,
  currentLocation,
  gpsTrail, // GPS breadcrumbs for actual traveled path
  mapCenter,
  mapZoom,
  showDefaultMarker,
  tripData,
  t,
  currentEntity,
  onForceRefresh,
  isrefreshing,
  isDriver,
  userPickupStopId = null,
  userDropoffStopId = null,
  filterWaypointsWithoutActivity = false,
  planRefreshTrigger = 0,
  latestPlanUpdate = null,
  etaData,
  onPlanStateChange = null,
  onLocalTripPlanUpdated = null,
  ...otherProps
}) => {
  const { userPreferences } = usePreferences();
  const tooltipPreference = userPreferences?.enable_tooltips;
  const hasTooltipPreference = typeof tooltipPreference === 'boolean';
  // Keep the authority source separate from its value. Hydration or removal of
  // a saved preference must reset transient map state even when its resolved
  // boolean happens to match the role fallback, while role changes must not
  // erase a manual override once a typed preference is authoritative.
  const tooltipAuthoritySource = hasTooltipPreference ? 'preference' : 'role-fallback';
  const tooltipAuthorityValue = hasTooltipPreference
    ? tooltipPreference
    : Boolean(isDriver);
  const [hasRouteReferenceLocations, setHasRouteReferenceLocations] = React.useState(false);
  const [routeReferenceLocations, setRouteReferenceLocations] = React.useState(null);
  // routeRefSource tracks where route data came from (plan vs reference) - kept for debugging
  const [routeRefSource, setRouteRefSource] = React.useState('unknown'); // eslint-disable-line no-unused-vars
  const [showTooltips, setShowTooltips] = React.useState(() => tooltipAuthorityValue);
  const [showVariantOverlays, setShowVariantOverlays] = React.useState(true);
  // progressIdx removed - GPS trail now shows actual traveled path instead of progress on planned route
  const mapRef = React.useRef(null);
  const [mapViewState, setMapViewState] = React.useState({
    center: mapCenter,
    zoom: mapZoom,
  });
  const userInteracting = React.useRef(false);
  const { distanceUnit } = useDateTimePreferences();

  // Get theme colors for map polylines
  const { colors, theme, cn = (...classes) => classes.filter(Boolean).join(' ') } = useTheme();
  const isDark = theme === 'dark';
  const mapColors = colors?.map || {
    routeAlternative: '#7C3AED', // Fallback purple
  };

  React.useEffect(() => {
    // The saved boolean is the normal authority; the role is only a degraded
    // fallback while that value is unavailable. Depend on both authority
    // primitives so source transitions are observed without resetting a
    // same-trip manual eye-button override on unrelated context or role churn.
    setShowTooltips(tooltipAuthorityValue);
  }, [tooltipAuthoritySource, tooltipAuthorityValue, tripData?.id]);

  // Trip plan state for segment variant selection
  const [tripPlan, setTripPlan] = React.useState(null);
  const [planStatus, setPlanStatus] = React.useState(null);
  const [planLoading, setPlanLoading] = React.useState(false);
  const planGeneration = React.useRef(0);
  const planInFlight = React.useRef(false);
  const planRequiresRetry = React.useRef(false);
  const planScope = React.useRef(null);
  const archiveScope = React.useRef(null);
  // A live plan can supersede HTTP publication without finishing that HTTP
  // request. Keep dialog ownership separate so its eventual settlement still
  // releases the spinner, while a response from an old trip cannot touch it.
  const activeConfirmation = React.useRef(null);
  const [isSelectingVariant, setIsSelectingVariant] = React.useState(false);
  const [isVariantPickerOpen, setIsVariantPickerOpen] = React.useState(false);
  const [pendingVariantSelection, setPendingVariantSelection] = React.useState(null);
  const displayStops = React.useMemo(() => getTripDisplayStops(tripData), [tripData]);
  const isEntityMismatch = isTripEntityMismatch(tripData, currentEntity);

  // Periodic tick so liveElapsedMinutes stays fresh between waypoint state changes.
  // Without this, Date.now() inside the useMemo only evaluates when dependencies
  // change, freezing elapsed time between waypoint events.
  const [nowTick, setNowTick] = React.useState(Date.now());
  React.useEffect(() => {
    if (tripData?.status !== 'in_progress') return;
    // Refresh immediately so a stale mount-time value is not used on the first
    // in_progress render (component may have mounted during dispatched status).
    setNowTick(Date.now());
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [tripData?.status]);

  // Determine if current user can select variants
  const showVariantAlternatives = !isEntityMismatch && canSelectVariants(tripData?.status, currentEntity?.userRole);

  const applyTripPlan = React.useCallback((plan) => {
    // Callers admit only their current request/socket/selection result. A first
    // trip can have a valid plan before recorded references exist, so missing
    // geometry is not evidence that this admitted plan needs another request.
    // Cached/remote collections must prove their shape before publishing the
    // plan: optional chaining cannot protect array methods from an object.
    // Nullish optional lists and missing geometry remain valid, but a supplied
    // malformed choice must reject the whole plan rather than silently vanish.
    const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
    const hasValidChoices = segment => isRecord(segment) && (
      segment.alternatives == null || (
        Array.isArray(segment.alternatives) && segment.alternatives.every(isRecord)
      )
    );
    if (!Array.isArray(plan?.segments) || plan.segments.length === 0 ||
        !plan.segments.every(hasValidChoices) ||
        (plan.bypassed_segments != null && (
          !Array.isArray(plan.bypassed_segments) || !plan.bypassed_segments.every(hasValidChoices)
        ))) {
      setTripPlan(null);
      setRouteReferenceLocations(null);
      setHasRouteReferenceLocations(false);
      setRouteRefSource('unknown');
      onPlanStateChange?.(null);
      return false;
    }

    setTripPlan(plan);
    setPlanStatus('ready');
    planRequiresRetry.current = false;
    onPlanStateChange?.(plan);

    // Geometry is replaced as a whole independently of plan acceptance. With
    // incomplete references, clear the old line and let the existing waypoint
    // connector render; never join partial fragments or retain another plan's
    // path while publishing the current plan's order and metrics.
    const completeGeometry = plan.segments.every(
      segment => Array.isArray(segment.polyline) && segment.polyline.length > 1
    );
    if (completeGeometry) {
      setRouteReferenceLocations(plan.segments.flatMap(segment => segment.polyline));
      setHasRouteReferenceLocations(true);
      setRouteRefSource('plan');
    } else {
      setRouteReferenceLocations(null);
      setHasRouteReferenceLocations(false);
      setRouteRefSource('unknown');
    }
    return true;
  }, [onPlanStateChange]);

  const requirePlanRefresh = React.useCallback(() => {
    activeConfirmation.current = null;
    ++planGeneration.current;
    planRequiresRetry.current = true;
    planInFlight.current = false;
    setPlanLoading(false);
    setTripPlan(null);
    setRouteReferenceLocations(null);
    setHasRouteReferenceLocations(false);
    setRouteRefSource('unknown');
    setPlanStatus('plan_refresh_required');
    setPendingVariantSelection(null);
    setIsVariantPickerOpen(false);
    setIsSelectingVariant(false);
    onPlanStateChange?.(null);
  }, [onPlanStateChange]);

  const filteredWaypoints = React.useMemo(() => {
    if (!selectedRoute?.waypoints?.length) return [];

    const sortedWaypoints = [...selectedRoute.waypoints]
      .sort((a, b) => getWaypointOrderValue(a) - getWaypointOrderValue(b));

    if (!isDriver) {
      return sortedWaypoints
        .filter((wp) => !isRouteDetailWaypoint(wp))
        .map((wp) => ({
          ...wp,
          // Rider maps should reflect the active booking selection, not stale
          // aggregate/default route flags left on the selectedRoute payload.
          is_user_pickup: hasStopId(userPickupStopId)
            ? isWaypointForStop(wp, userPickupStopId)
            : wp.is_user_pickup === true,
          is_user_dropoff: hasStopId(userDropoffStopId)
            ? isWaypointForStop(wp, userDropoffStopId)
            : wp.is_user_dropoff === true
        }));
    }

    if (!filterWaypointsWithoutActivity) {
      return sortedWaypoints;
    }

    return sortedWaypoints.filter((wp) => !isRouteDetailWaypoint(wp));
  }, [selectedRoute?.waypoints, isDriver, filterWaypointsWithoutActivity, userPickupStopId, userDropoffStopId]);

  // Compute GPS trail traveled distance in meters (haversine sum)
  const gpsTrailDistanceM = React.useMemo(() => {
    if (!gpsTrail || gpsTrail.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < gpsTrail.length; i++) {
      const prev = gpsTrail[i - 1];
      const curr = gpsTrail[i];
      const lat1 = (prev.lat ?? prev.latitude ?? 0) * Math.PI / 180;
      const lat2 = (curr.lat ?? curr.latitude ?? 0) * Math.PI / 180;
      const dLat = lat2 - lat1;
      const dLng = ((curr.lng ?? curr.longitude ?? 0) - (prev.lng ?? prev.longitude ?? 0)) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      total += 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
    return total;
  }, [gpsTrail]);

  const liveElapsedMinutes = React.useMemo(() => {
    if (tripData?.status !== 'in_progress' || displayStops.length === 0) return null;

    const sortedStops = [...displayStops].sort((a, b) => {
      const seqA = a.effective_sequence ?? a.sequence_number ?? 0;
      const seqB = b.effective_sequence ?? b.sequence_number ?? 0;
      return seqA - seqB;
    });

    const startStop = sortedStops.find((stop) =>
      stop.stop_position === 'start' ||
      stop.is_departure === true ||
      stop.sequence_number === 0 ||
      stop.point_type === 'start'
    ) || sortedStops[0];

    // effective_stops can lag behind live WebSocket passage updates, so use
    // tripData.stops as the authoritative timestamp source when available.
    const liveStopsById = new Map(
      (tripData?.stops || [])
        .filter((stop) => stop?.waypoint_id != null)
        .map((stop) => [stop.waypoint_id, stop])
    );
    const getLiveStop = (stop) => liveStopsById.get(stop?.waypoint_id);

    const startDepartureTs = getLiveStop(startStop)?.waypoint_departure_time
      || startStop?.waypoint_departure_time
      || getLiveStop(startStop)?.waypoint_arrival_time
      || startStop?.waypoint_arrival_time;
    if (!startDepartureTs) return null;
    const startMs = new Date(startDepartureTs).getTime();
    if (!Number.isFinite(startMs)) return null;

    // If currently dwelling at a stop (arrived but not departed), freeze elapsed
    // at that arrival time; otherwise elapsed continues with real time.
    const currentDwellingMs = sortedStops.reduce((maxMs, stop) => {
      const liveStop = getLiveStop(stop);
      const arrivalTs = liveStop?.waypoint_arrival_time || stop?.waypoint_arrival_time;
      const departureTs = liveStop?.waypoint_departure_time || stop?.waypoint_departure_time;
      if (!arrivalTs || departureTs) return maxMs;
      const ms = new Date(arrivalTs).getTime();
      if (!Number.isFinite(ms)) return maxMs;
      return Math.max(maxMs, ms);
    }, 0);

    const endMs = currentDwellingMs > 0 ? currentDwellingMs : nowTick;
    return Math.max(0, (endMs - startMs) / 60000);
  }, [displayStops, tripData?.status, tripData?.stops, nowTick]);

  // Live overlay data for in-progress trips: traveled + remaining.
  // This is the primary duration source; plan totals are only a fallback.
  const liveOverlayWaypoints = etaData?.live?.waypoints || etaData?.websocket?.waypoints || null;

  const liveOverlay = React.useMemo(() => {
    if (tripData?.status !== 'in_progress') return null;

    // Get remaining distance/ETA from last ETA waypoint
    const waypoints = liveOverlayWaypoints;
    if (!waypoints?.length) return null;
    const lastWp = waypoints[waypoints.length - 1];
    const remainingDistanceM = parseNullableNonNegativeMetric(lastWp?.distance_meters_from_current);
    const remainingMinutes = parseNullableNonNegativeMetric(lastWp?.eta_minutes_from_now);

    return {
      totalDistanceM: remainingDistanceM === null ? null : gpsTrailDistanceM + remainingDistanceM,
      totalDurationMin: remainingMinutes === null || liveElapsedMinutes === null ? null : liveElapsedMinutes + remainingMinutes
    };
  }, [tripData?.status, liveOverlayWaypoints, gpsTrailDistanceM, liveElapsedMinutes]);

  const formatDistanceWithUnit = React.useCallback((meters) => {
    if (parseNullableNonNegativeMetric(meters) === null) return t('N/A');
    return formatDistanceMeters(meters, distanceUnit, t, {
      compact: false,
      zeroLabel: getZeroDistanceLabel(distanceUnit, t)
    });
  }, [distanceUnit, t]);

  const formatAlternativeDistance = React.useCallback((meters) => {
    const numericMeters = Number(meters);
    // A null route-segment distance means the static authority is unavailable,
    // not that this alternative is zero length. Keep an actual numeric zero
    // distinct so route pickers, tooltips, and confirmation all agree.
    if (meters === null || meters === undefined || meters === '' || !Number.isFinite(numericMeters)) {
      return t('N/A');
    }

    return formatDistanceWithUnit(numericMeters);
  }, [formatDistanceWithUnit, t]);

  const formatMinutesLocalized = React.useCallback((minutes) => {
    const value = parseNullableNonNegativeMetric(minutes);
    return value === null ? t('N/A') : `${Math.round(value)}${t('min')}`;
  }, [t]);
  const formatSecondsLocalized = React.useCallback((seconds) => {
    const value = parseNullableNonNegativeMetric(seconds);
    return formatMinutesLocalized(value === null ? null : value / 60);
  }, [formatMinutesLocalized]);

  const getRouteChoiceLabel = React.useCallback((alternative) => {
    if (alternative?.variant) return alternative.variant;
    return alternative?.isSequential ? t('sequentialRoute') : t('routeShortcut');
  }, [t]);

  const getRouteVariantOptionLabel = React.useCallback((segment, alternative) => {
    const variantLabel = getRouteChoiceLabel(alternative);
    const toWaypointName = alternative.to_waypoint_name || segment.to_waypoint_name;
    const spanLabel = t('spanningTo', {
      from: segment.from_waypoint_name,
      to: toWaypointName
    });
    const distanceLabel = `${t('totalDistance')}: ${formatAlternativeDistance(alternative.distance_m)}`;
    const durationLabel = `${t('totalDuration')}: ${formatSecondsLocalized(alternative.duration_s)}`;

    // The modal list is the keyboard/touch equivalent of the map overlay
    // tooltip. Put the same route identity and metrics in the accessible name
    // so the choice is complete without hover.
    return [
      variantLabel,
      spanLabel,
      distanceLabel,
      durationLabel,
      alternative.is_spanning ? t('spanningVariant') : null
    ].filter(Boolean).join('. ');
  }, [formatAlternativeDistance, formatSecondsLocalized, getRouteChoiceLabel, t]);

  const overlayDistanceM = tripData?.status === 'in_progress'
    ? liveOverlay?.totalDistanceM ?? null : tripPlan?.totals?.distance_m ?? null;
  const overlayDurationMin = tripData?.status === 'in_progress'
    ? liveOverlay?.totalDurationMin ?? null : liveOverlay?.totalDurationMin != null
    ? liveOverlay.totalDurationMin
    : (parseNullableNonNegativeMetric(tripPlan?.totals?.duration_s) === null ? null : tripPlan.totals.duration_s / 60);

  const departedWaypointIds = React.useMemo(() => new Set(
    (tripData?.stops || [])
      .filter((stop) => stop?.waypoint_departure_time)
      .map((stop) => Number(stop.waypoint_id))
      .filter(Number.isFinite)
  ), [tripData?.stops]);

  const getSelectableAlternatives = React.useCallback((segment) => (
    segment?.alternatives?.filter((alternative) => (
      alternative.polyline?.length > 1
      // The cached plan remains presentation input only. Current trip-stop
      // departure is a reversible hint that this exact choice target is behind
      // the vehicle; locked validation still owns mutation eligibility.
      && !departedWaypointIds.has(Number(alternative.to_waypoint_id))
    )) || []
  ), [departedWaypointIds]);

  const selectableFamilies = React.useMemo(() => {
    const families = new Map();
    for (const segment of tripPlan?.segments || []) {
      for (const alternative of getSelectableAlternatives(segment)) {
        const key = [
          segment.from_waypoint_id,
          alternative.to_waypoint_id,
          alternative.archived_type
        ].join('-');
        if (!families.has(key)) {
          families.set(key, {
            key,
            segment,
            toWaypointId: alternative.to_waypoint_id,
            toWaypointName: alternative.to_waypoint_name,
            alternatives: []
          });
        }
        families.get(key).alternatives.push(alternative);
      }
    }
    return [...families.values()];
  }, [tripPlan?.segments, getSelectableAlternatives]);

  const getConflictingVariantKey = React.useCallback((segment) => {
    if (!tripPlan?.bypassed_waypoint_ids?.length) {
      return null;
    }

    const bypassed = new Set(tripPlan.bypassed_waypoint_ids);
    if (!bypassed.has(segment.from_waypoint_id)) {
      return null;
    }

    const conflictingSegment = tripPlan.segments?.find(s => (
      s.isSequential === false
      && Number(s.from_waypoint_id) !== Number(segment.from_waypoint_id)
    ));
    return conflictingSegment
      ? (conflictingSegment.variant || t('routeShortcut'))
      : null;
  }, [tripPlan, t]);

  const openVariantConfirmation = React.useCallback((segment, alternative) => {
    if (activeConfirmation.current || isSelectingVariant) return;

    setPendingVariantSelection({
      segment,
      alternative,
      targetToWpId: alternative.to_waypoint_id,
      conflictVariantKey: getConflictingVariantKey(segment)
    });
    setIsVariantPickerOpen(false);
  }, [getConflictingVariantKey, isSelectingVariant]);

  const handleConfirmVariantSelection = React.useCallback(async () => {
    if (!pendingVariantSelection || activeConfirmation.current || isEntityMismatch) return;

    const { segment, alternative, targetToWpId } = pendingVariantSelection;
    const owner = { planGeneration: ++planGeneration.current };
    // Reserve synchronously: two clicks before React renders still own one PUT.
    activeConfirmation.current = owner;
    setIsSelectingVariant(true);
    try {
      const response = await axios.put(
        `/api/trips/${currentEntity.entityId}/trips/${tripData.id}/segments/${segment.from_waypoint_id}/${targetToWpId}/archived-type`,
        {
          archivedType: alternative.archived_type,
          variant: alternative.variant,
          isSequential: alternative.isSequential
        }
      );

      if (activeConfirmation.current !== owner) return;
      setPendingVariantSelection(null);
      if (owner.planGeneration !== planGeneration.current) return;
      const plan = response.data?.plan;
      if (!plan) { requirePlanRefresh(); return; }
      if (applyTripPlan(plan)) {
        onLocalTripPlanUpdated?.(plan);
        const variantLabel = getRouteChoiceLabel(alternative);
        const selectedMessage = t('routeVariantSelectedWithName', { variant: variantLabel });
        toast.success(alternative.is_spanning
          ? `${selectedMessage} (${t('spanningTo', { from: segment.from_waypoint_name, to: alternative.to_waypoint_name || '' })})`
          : selectedMessage);
      } else { requirePlanRefresh(); return; }
      setPendingVariantSelection(null);
    } catch (error) {
      if (activeConfirmation.current !== owner) return;
      logger.error('[TripTrackingMap] Failed to select variant:', error);
      const code = error.response?.data?.code || error.response?.data?.error;
      if (owner.planGeneration !== planGeneration.current) {
        // Report this request's failure without invalidating the newer plan.
        setPendingVariantSelection(null);
      } else if (!CLOSE_ONLY_ROUTE_CHOICE_CODES.has(code)
          && (error.response?.status === 409 || STALE_ROUTE_CHOICE_CODES.has(code) || code === 'TRIP_PLAN_REFRESH_SUPERSEDED')) {
        requirePlanRefresh();
      } else if (CLOSE_ONLY_ROUTE_CHOICE_CODES.has(code)) {
        // These eligibility failures do not prove the cached plan is stale.
        // Close the obsolete confirmation without refetching or retrying.
        setPendingVariantSelection(null);
      }
      toast.error(t(ROUTE_CHOICE_ERROR_KEYS[code] || 'failedToSelectVariant'));
    } finally {
      if (activeConfirmation.current === owner) {
        activeConfirmation.current = null;
        setIsSelectingVariant(false);
      }
    }
  }, [currentEntity?.entityId, tripData?.id, pendingVariantSelection, isEntityMismatch, t, applyTripPlan, requirePlanRefresh, onLocalTripPlanUpdated, getRouteChoiceLabel]);

  logger.debug('[TripTrackingMap] Component rendering', {
    hasTripData: !!tripData,
    tripStatus: tripData?.status,
    hasSelectedRoute: !!selectedRoute,
    hasMapCenter: Array.isArray(mapCenter),
    mapZoom
  });

  React.useEffect(() => {
    if (!userInteracting.current) {
      setMapViewState({
        center: mapCenter,
        zoom: mapZoom,
      });
    }
  }, [mapCenter, mapZoom]);

  const handleMapReady = React.useCallback((mapApi) => {
    logger.debug('[TripTrackingMap] handleMapReady called', {
      hasMapApi: Boolean(mapApi)
    });

    if (!mapApi) {
      logger.debug('[TripTrackingMap] handleMapReady called with null/undefined mapApi');
      return;
    }
    logger.debug('[TripTrackingMap] mapApi shape', {
      type: typeof mapApi,
      hasOnMethod: typeof mapApi.on === 'function'
    });
    
    mapRef.current = mapApi;
  }, []);

  // Fetch route-reference-locations for completed/canceled trips (shows actual traveled path)
  const fetchRouteReferenceLocations = React.useCallback(async () => {
    const routeId = tripData?.route_id;

    if (!routeId || !currentEntity?.entityId || isEntityMismatch) {
      logger.debug('[TripTrackingMap] Missing required IDs for route references');
      return;
    }

    const owner = ++planGeneration.current;
    try {
      logger.debug('[TripTrackingMap] Fetching route reference locations', {
        routeId,
        tripId: tripData?.id,
        status: tripData?.status
      });
      const response = await axios.get(
        `/api/trips/${currentEntity.entityId}/${routeId}/route-reference-locations`,
        {
          params: {
            archived_type: currentEntity.archived_type || 'representative',
            tripId: tripData?.id,
            status: tripData?.status
          }
        }
      );
      if (owner !== planGeneration.current) return;
      const locations = response.data?.data || [];
      if (locations.length > 0) {
        const points = locations.map((loc) => [
          parseFloat(loc.latitude),
          parseFloat(loc.longitude),
        ]);
        setRouteReferenceLocations(points);
        setHasRouteReferenceLocations(true);
        setRouteRefSource('reference');
        logger.debug('[TripTrackingMap] Loaded route reference locations', {
          pointCount: points.length
        });
      } else {
        logger.debug('[TripTrackingMap] No route reference locations found');
      }
    } catch (error) {
      if (owner !== planGeneration.current) return;
      logger.error('[TripTrackingMap] Error fetching route reference locations:', error);
      toast.error(t('failedToLoadRouteReferenceLocations'));
    }
  }, [currentEntity?.entityId, currentEntity?.archived_type, tripData?.route_id, tripData?.id, tripData?.status, isEntityMismatch, t]);

  // Fetch trip plan for non-completed trips (includes segment alternatives)
  const fetchTripPlan = React.useCallback(async ({ retry = false } = {}) => {
    if (planRequiresRetry.current && !retry) return;
    if (!tripData?.id || !currentEntity?.entityId || isEntityMismatch) {
      logger.debug('[TripTrackingMap] Missing required IDs for trip plan');
      return;
    }
    if (retry && planInFlight.current) return;
    const owner = ++planGeneration.current;
    planInFlight.current = true;
    setPlanLoading(true);

    try {
      logger.debug('[TripTrackingMap] Fetching trip plan', {
        tripId: tripData.id
      });
      const response = await axios.get(
        `/api/trips/${currentEntity.entityId}/trips/${tripData.id}/plan`
      );
      if (owner !== planGeneration.current) return;
      const plan = response.data?.plan;

      if (applyTripPlan(plan)) {
        const stitchedCount = plan.segments.flatMap(seg => seg.polyline || []).length;
        logger.debug('[TripTrackingMap] Loaded trip plan', {
          segmentCount: plan.segments.length,
          stitchedPointCount: stitchedCount
        });
      } else {
        requirePlanRefresh();
      }
    } catch (error) {
      if (owner !== planGeneration.current) return;
      logger.error('[TripTrackingMap] Error fetching trip plan:', error);
      requirePlanRefresh();
      if (error.response?.status === 404) setPlanStatus('missing');
    } finally {
      if (owner === planGeneration.current) {
        planInFlight.current = false;
        setPlanLoading(false);
      }
    }
  }, [currentEntity?.entityId, tripData?.id, isEntityMismatch, applyTripPlan, requirePlanRefresh]);

  // Refresh plan when parent signals websocket plan/bookings updates.
  React.useEffect(() => {
    if (!planRefreshTrigger || !tripData?.id || isEntityMismatch) return;
    if (tripData?.status === 'completed' || tripData?.status === 'canceled') return;
    fetchTripPlan();
  }, [planRefreshTrigger, tripData?.id, tripData?.status, isEntityMismatch, fetchTripPlan]);

  // Apply websocket trip-plan payloads immediately so active-trip routes/totals stay live.
  React.useEffect(() => {
    if (tripData?.status === 'completed' || tripData?.status === 'canceled') return;
    if (!latestPlanUpdate || latestPlanUpdate?.tripId !== tripData?.id) return;
    if (!latestPlanUpdate.plan) { requirePlanRefresh(); return; }
    ++planGeneration.current;
    planInFlight.current = false;
    setPlanLoading(false);
    if (!applyTripPlan(latestPlanUpdate.plan)) requirePlanRefresh();
  }, [tripData?.status, tripData?.id, latestPlanUpdate, applyTripPlan, requirePlanRefresh]);

  React.useEffect(() => {
    const key = `${currentEntity?.entityId}:${tripData?.id}`;
    const archive = tripData?.selected_archived_type || 'representative';
    if (archiveScope.current?.key === key && archiveScope.current.archive !== archive) requirePlanRefresh();
    archiveScope.current = { key, archive };
  }, [currentEntity?.entityId, tripData?.id, tripData?.selected_archived_type, requirePlanRefresh]);

  // Only semantic scope changes retire a confirmation. Fetch callbacks may
  // change identity in the same scope and must not strand a submitted dialog.
  React.useEffect(() => {
    activeConfirmation.current = null;
    setPendingVariantSelection(null);
    setIsVariantPickerOpen(false);
    setIsSelectingVariant(false);
    return () => { activeConfirmation.current = null; };
  }, [currentEntity?.entityId, tripData?.id, tripData?.route_id, tripData?.status, isEntityMismatch]);

  // Main effect to fetch route data based on trip status
  React.useEffect(() => () => {
    ++planGeneration.current;
    planInFlight.current = false;
  }, [currentEntity?.entityId, tripData?.id, tripData?.status]);

  React.useEffect(() => {
    // Reset state
    const key = `${currentEntity?.entityId}:${tripData?.id}`;
    if (planScope.current !== key) planRequiresRetry.current = false;
    planScope.current = key;
    if (planRequiresRetry.current) return;
    ++planGeneration.current;
    planInFlight.current = false;
    setPlanStatus(null);
    setRouteReferenceLocations(null);
    setHasRouteReferenceLocations(false);
    setTripPlan(null);
    onPlanStateChange?.(null);
    setRouteRefSource('unknown');

    if (isEntityMismatch) {
      return;
    }

    const status = tripData?.status;

    // For completed/canceled trips, use route-reference-locations (shows actual traveled path)
    if (status === 'completed' || status === 'canceled') {
      if (status === 'completed') {
        fetchRouteReferenceLocations();
      }
      // For canceled trips, don't show route (per docs/2026-01-06-SEGMENT_VARIANT_MAP_DISPLAY.md)
      return;
    }

    // For other statuses, fetch the trip plan (includes segment alternatives)
    if (tripData?.id) {
      fetchTripPlan();
    }
    return () => { ++planGeneration.current; planInFlight.current = false; };
  }, [currentEntity?.entityId, tripData?.id, tripData?.route_id, tripData?.status, isEntityMismatch, fetchRouteReferenceLocations, fetchTripPlan, onPlanStateChange]);

  // progressIdx calculation removed - GPS trail now shows actual traveled path
  // See backend/docs/2026-02-05-GPS_BREADCRUMBS_MAP_DISPLAY.md

  return (
    <div className="relative w-full h-full">
      {/* Keep the recovery message clear of Leaflet's left-hand zoom controls. */}
      {planStatus === 'plan_refresh_required' && (
        <div className="absolute left-14 right-2 top-2 z-[1000] rounded bg-white p-3 text-sm text-gray-800 shadow" role="status">
          {t('planRefreshRequired')}
          <button type="button" className="ml-3 rounded border px-3 py-2" disabled={planLoading}
            onClick={() => fetchTripPlan({ retry: true })}>{t('retryPlan')}</button>
        </div>
      )}
      <MapWithTooltips
        selectedRoute={{
          ...selectedRoute,
          waypoints: filteredWaypoints
        }}
        tripStatus={tripData?.status}
        currentLocation={currentLocation}
        mapCenter={mapViewState.center}
        mapZoom={mapViewState.zoom}
        showDefaultMarker={showDefaultMarker}
        t={t}
        onMapReady={handleMapReady}
        showTooltips={showTooltips}
        entityCoordinates={mapCenter}
        hasRouteReferenceLocations={hasRouteReferenceLocations}
        isDriver={isDriver}
        currentEntity={currentEntity}
        // pickupStops={pickupStops}
        // dropoffStops={dropoffStops}
        {...otherProps}
      >
        {((routeReferenceLocations && routeReferenceLocations.length > 1) || (gpsTrail && gpsTrail.length > 1)) && (
          <TripRouteSegments
            key={`${tripData?.id || 'trip'}-${tripData?.status || 'status'}`} // Force re-mount on status change (e.g., completed)
            routeReferenceLocations={routeReferenceLocations}
            selectedRoute={selectedRoute}
            tripData={tripData}
            gpsTrail={gpsTrail}
            isDriver={isDriver}
          />
        )}
        {/* Render alternative polylines (Google Maps style) - driver/admin only, NOT rider */}
        {/* Spanning variants show full polyline with span target info in tooltip */}
        {showVariantAlternatives && showVariantOverlays && tripPlan?.segments?.map(segment => (
          segment.has_alternatives && getSelectableAlternatives(segment)
            .map(alt => (
              <Polyline
                key={`alt-${getRouteChoiceKey(segment.from_waypoint_id, alt)}`}
                positions={alt.polyline}
                pane="markerPane"
                interactive={true}
                pathOptions={{
                  color: mapColors.routeAlternative,
                  weight: alt.is_spanning ? 5 : 4, // Slightly thicker for spanning
                  opacity: 0.9,
                  dashArray: alt.is_spanning ? '8 4' : undefined, // Dashed for spanning
                }}
                eventHandlers={{
                  click: (e) => {
                    e.originalEvent?.stopPropagation?.();
                    logger.debug('[TripTrackingMap] Alternative polyline clicked', {
                      hasNamedVariant: Boolean(alt.variant),
                      isSequential: alt.isSequential,
                      isSpanning: Boolean(alt.is_spanning)
                    });
                    openVariantConfirmation(segment, alt);
                  },
                  mouseover: (e) => {
                    e.target.setStyle({ weight: alt.is_spanning ? 7 : 6, opacity: 1 });
                  },
                  mouseout: (e) => {
                    e.target.setStyle({
                      weight: alt.is_spanning ? 5 : 4,
                      opacity: 0.9,
                    });
                  }
                }}
              >
                <Tooltip sticky>
                  <div style={{ cursor: 'pointer' }}>
                    <strong>{getRouteChoiceLabel(alt)}</strong>
                    {/* Show from/to waypoint names for all variants */}
                    <span style={{ color: alt.is_spanning ? '#9333ea' : '#6b7280', marginLeft: '4px' }}>
                      ({t('spanningTo', { from: segment.from_waypoint_name, to: alt.to_waypoint_name || segment.to_waypoint_name })})
                    </span>
                    <br/>
                    {formatAlternativeDistance(alt.distance_m)}, {formatSecondsLocalized(alt.duration_s)}<br/>
                    <em style={{ color: mapColors.routeAlternative }}>{t('clickToReviewRouteChange')}</em>
                  </div>
                </Tooltip>
              </Polyline>
            ))
        ))}
        {/* Render bypassed segment alternatives as dashed context for waypoints currently
            bypassed by a spanning variant. These are not independently selectable. */}
        {showVariantAlternatives && showVariantOverlays && tripPlan?.bypassed_segments?.map(segment => (
          segment.has_alternatives && segment.alternatives
            ?.filter(alt => alt.polyline?.length > 1)
            .map(alt => (
              <Polyline
                key={`bp-alt-${getRouteChoiceKey(segment.from_waypoint_id, alt)}`}
                positions={alt.polyline}
                pane="markerPane"
                interactive={true}
                pathOptions={{
                  color: mapColors.routeAlternative,
                  weight: 3,
                  opacity: 0.5,
                  dashArray: '6 6', // Always dashed — these are bypassed
                }}
                eventHandlers={{
                  mouseover: (e) => {
                    e.target.setStyle({ weight: 5, opacity: 0.8 });
                  },
                  mouseout: (e) => {
                    e.target.setStyle({ weight: 3, opacity: 0.5 });
                  }
                }}
              >
                <Tooltip sticky>
                  <div>
                    <strong>{segment.from_waypoint_name} → {segment.to_waypoint_name}</strong>
                    <span style={{ color: '#dc2626', marginLeft: '4px' }}>
                      ({t('variantsBypassedWaypoints', { count: tripPlan?.bypassed_waypoint_ids?.length || 0 })})
                    </span>
                    <br/>
                    {getRouteChoiceLabel(alt)}: {formatAlternativeDistance(alt.distance_m)}, {formatSecondsLocalized(alt.duration_s)}
                  </div>
                </Tooltip>
              </Polyline>
            ))
        ))}
        <MapControls
          mapRef={mapRef}
          showTooltips={showTooltips}
          onToggleTooltips={setShowTooltips}
          isDriver={isDriver}
          onForceRefresh={onForceRefresh}
          isrefreshing={isrefreshing}
          t={t}
        />
      </MapWithTooltips>
      {showVariantAlternatives && selectableFamilies.length > 0 && (
        <button
          onClick={() => setIsVariantPickerOpen(true)}
          title={t('routeVariantPickerTitle')}
          aria-label={t('routeVariantPickerTitle')}
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          style={{
            position: 'absolute',
            bottom: '30px',
            left: '10px',
            zIndex: 1000,
            width: '34px',
            height: '34px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: showVariantOverlays ? '#3b82f6' : 'white',
            color: showVariantOverlays ? 'white' : '#666',
            border: '2px solid rgba(0,0,0,0.2)',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <GitBranch size={18} />
        </button>
      )}
      {/* Totals overlay — prefer whole-trip plan duration when available. */}
      {tripData?.status !== 'completed' && tripData?.status !== 'canceled' && (overlayDistanceM != null || overlayDurationMin != null) && (
        <div
          data-testid="trip-map-totals-overlay"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 1000,
            backgroundColor: isDark ? 'rgba(17,24,39,0.85)' : 'white',
            color: isDark ? '#e5e7eb' : '#111827',
            padding: '8px 12px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            fontSize: '12px',
            lineHeight: 1.35,
            maxWidth: 'calc(100% - 20px)',
            overflowWrap: 'anywhere',
            pointerEvents: 'none'
          }}
        >
          {overlayDistanceM != null && (
            <div><strong>{t('totalDistance')}:</strong> {formatDistanceWithUnit(overlayDistanceM)}</div>
          )}
          {overlayDurationMin != null && (
            <div><strong>{t('totalDuration')}:</strong> {formatMinutesLocalized(overlayDurationMin)}</div>
          )}
        </div>
      )}
      <Modal
        isOpen={isVariantPickerOpen}
        onClose={() => setIsVariantPickerOpen(false)}
        title={t('routeVariantPickerTitle')}
        size="lg"
      >
        <div className="space-y-4">
          <label className={cn('flex items-center gap-2 cursor-pointer', isDark ? 'text-gray-200' : 'text-slate-700')}>
            <input
              type="checkbox"
              checked={showVariantOverlays}
              onChange={() => setShowVariantOverlays(prev => !prev)}
              className="rounded"
            />
            <span className="text-sm font-medium">{t('showVariants')}</span>
          </label>
          {selectableFamilies.map((family) => (
            <div key={`picker-${family.key}`} className={cn('rounded-lg border p-3', isDark ? 'border-gray-600' : 'border-slate-200')}>
              <div className="mb-3">
                <div className={cn('text-sm font-semibold', isDark ? 'text-gray-100' : 'text-slate-900')}>
                  {family.segment.from_waypoint_name}{' -> '}{family.toWaypointName}
                </div>
                <div
                  id={`route-variant-help-${family.segment.from_waypoint_id}-${family.toWaypointId}`}
                  className={cn('text-xs', isDark ? 'text-gray-400' : 'text-slate-500')}
                >
                  {t('routeVariantSegmentHelp')}
                </div>
              </div>
              <div className="space-y-2">
                {family.alternatives.map((alternative) => {
                  const helpId = `route-variant-help-${family.segment.from_waypoint_id}-${family.toWaypointId}`;

                  return (
                    <button
                      key={`picker-alt-${getRouteChoiceKey(family.segment.from_waypoint_id, alternative)}`}
                      onClick={() => openVariantConfirmation(family.segment, alternative)}
                      aria-label={getRouteVariantOptionLabel(family.segment, alternative)}
                      aria-describedby={helpId}
                      className={cn('w-full rounded-md border px-3 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                        isDark ? 'border-gray-600 hover:bg-gray-700 focus-visible:ring-offset-gray-900' : 'border-slate-200 hover:bg-slate-50 focus-visible:ring-offset-white'
                      )}
                      style={{ borderLeft: `4px solid ${mapColors.routeAlternative}` }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className={cn('font-medium', isDark ? 'text-gray-100' : 'text-slate-900')}>
                            {getRouteChoiceLabel(alternative)}
                          </div>
                          <div className={cn('text-xs', isDark ? 'text-gray-400' : 'text-slate-500')}>
                            {t('spanningTo', {
                              from: family.segment.from_waypoint_name,
                              to: alternative.to_waypoint_name
                            })}
                          </div>
                        </div>
                        {alternative.is_spanning && (
                          <span className={cn('rounded-full px-2 py-1 text-[11px] font-medium',
                            isDark ? 'bg-violet-900 text-violet-200' : 'bg-violet-100 text-violet-700'
                          )}>
                            {t('spanningVariant')}
                          </span>
                        )}
                      </div>
                      <div className={cn('mt-2 text-xs', isDark ? 'text-gray-400' : 'text-slate-600')}>
                        {formatAlternativeDistance(alternative.distance_m)} · {formatSecondsLocalized(alternative.duration_s)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Modal>
      <ConfirmDialogShell
        isOpen={Boolean(pendingVariantSelection)}
        onClose={() => {
          if (!activeConfirmation.current) setPendingVariantSelection(null);
        }}
        onConfirm={handleConfirmVariantSelection}
        title={t('confirmRouteVariantTitle')}
        confirmLabel={t('Confirm')}
        cancelLabel={t('Cancel')}
        confirmVariant={pendingVariantSelection?.conflictVariantKey
          || (pendingVariantSelection?.alternative?.isSequential === false
            && pendingVariantSelection?.alternative?.bypassed_waypoint_ids?.length > 0)
          ? 'warning'
          : 'primary'}
        showIcon={true}
        iconVariant={pendingVariantSelection?.conflictVariantKey
          || (pendingVariantSelection?.alternative?.isSequential === false
            && pendingVariantSelection?.alternative?.bypassed_waypoint_ids?.length > 0)
          ? 'warning'
          : 'info'}
        isLoading={isSelectingVariant}
      >
        {pendingVariantSelection && (
          <div className="space-y-3 text-sm">
            <p className={isDark ? 'text-gray-300' : 'text-slate-700'}>
              {t('confirmRouteVariantMessage', {
                variant: getRouteChoiceLabel(pendingVariantSelection.alternative),
                from: pendingVariantSelection.segment.from_waypoint_name,
                to: pendingVariantSelection.alternative.to_waypoint_name || pendingVariantSelection.segment.to_waypoint_name
              })}
            </p>
            <div className={cn('rounded-md border p-3',
              isDark ? 'border-gray-600 bg-gray-800 text-gray-300' : 'border-slate-200 bg-slate-50 text-slate-700'
            )}>
              <div><strong>{t('routeVariantLabel')}:</strong> {getRouteChoiceLabel(pendingVariantSelection.alternative)}</div>
              <div><strong>{t('routeVariantFromLabel')}:</strong> {pendingVariantSelection.segment.from_waypoint_name}</div>
              <div><strong>{t('routeVariantToLabel')}:</strong> {pendingVariantSelection.alternative.to_waypoint_name || pendingVariantSelection.segment.to_waypoint_name}</div>
              <div><strong>{t('totalDistance')}:</strong> {formatAlternativeDistance(pendingVariantSelection.alternative.distance_m)}</div>
              <div><strong>{t('totalDuration')}:</strong> {formatSecondsLocalized(pendingVariantSelection.alternative.duration_s)}</div>
              <div><strong>{t('spanningVariant')}:</strong> {pendingVariantSelection.alternative.is_spanning ? t('yes') : t('no')}</div>
            </div>
            {pendingVariantSelection.conflictVariantKey && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
                {t('routeVariantConflictWarning', { variant: pendingVariantSelection.conflictVariantKey })}
              </div>
            )}
            {pendingVariantSelection.alternative.isSequential === false
              && pendingVariantSelection.alternative.bypassed_waypoint_ids?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
                {t('routeChoiceBypassDelayWarning')}
              </div>
            )}
          </div>
        )}
      </ConfirmDialogShell>
    </div>
  );
};

export default TripTrackingMap;
